import * as Sentry from '@sentry/react';
import type { ErrorInfo } from 'react';
import APP_PACKAGE from '../../package.json';
import { env } from '@/config/env';

/** Initialize Sentry outside local dev, and only when a DSN is configured. */
export function initSentry() {
  if (env.appEnv === 'development' && env.isDev) return;
  if (!env.sentryDsn) return;

  // Performance tracing + session replay run on PRODUCTION only. Staging (and any
  // non-prod env) get error capture without tracing — no browserTracing/replay,
  // sample rates 0.
  const isProd = env.appEnv === 'production';

  Sentry.init({
    dsn: env.sentryDsn,
    integrations: isProd
      ? [Sentry.browserTracingIntegration(), Sentry.replayIntegration()]
      : [],
    environment: env.appEnv,
    release: `build-${APP_PACKAGE.version}.${env.appEnv}`,
    tracesSampleRate: isProd ? 1.0 : 0,
    replaysSessionSampleRate: isProd ? 1.0 : 0,
    replaysOnErrorSampleRate: isProd ? 1.0 : 0,
    sendDefaultPii: true,
  });
}

/**
 * Attach the signed-in user to every Sentry event (id + email + name). Pass null
 * on logout to clear it. Called from AuthProvider so it covers all login paths
 * AND session restore on reload.
 */
export function setSentryUser(
  user: { uuid: string; email?: string; name?: string } | null,
): void {
  if (!user) {
    Sentry.setUser(null);
    return;
  }
  Sentry.setUser({ id: user.uuid, email: user.email, username: user.name });
}

/**
 * Reporter for the @vnetwork/ui <ErrorBoundary onError={...}>. The boundary only
 * forwards caught render errors to its `onError` prop — without this, crashes are
 * swallowed by the fallback and never reach Sentry.
 */
export function reportErrorBoundary(error: Error, info: ErrorInfo) {
  Sentry.captureException(error, {
    contexts: { react: { componentStack: info.componentStack } },
  });
}
